import { TEAM_GROUPS, iniciales } from '../lib/team';

// Parte C de <section id="nosotros">.
//
// OJO: no tiene useEffect propio a propósito. Las animaciones viven en el
// useEffect de AboutSection, cuyo gsap.context() está acotado a toda la
// sección, así que sus selectores (.team-*) alcanzan este árbol. Si algún día
// este componente sale de #nosotros, esas animaciones dejarán de aplicar EN
// SILENCIO: hay que moverlas aquí con su propio gsap.context.
export default function TeamGrid() {
  return (
    <div className="team-block relative w-full bg-[#f5f0e8] py-28 md:py-36">
      {/* Duotono a las dos tintas de marca. Los valores no son arbitrarios:
          0.059/0.078/0.098 es #0F1419 normalizado y 0.941/0.922/0.894 es
          #F0EBE4. Nada de dorado como luz alta: sobre tonos de piel se ve
          ictérico, así que el dorado se queda en el chrome. */}
      <svg width="0" height="0" aria-hidden="true" focusable="false" className="absolute">
        <filter id="team-duotone" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0.2126 0.7152 0.0722 0 0
                    0.2126 0.7152 0.0722 0 0
                    0.2126 0.7152 0.0722 0 0
                    0      0      0      1 0"
          />
          <feComponentTransfer>
            <feFuncR type="table" tableValues="0.059 0.941" />
            <feFuncG type="table" tableValues="0.078 0.922" />
            <feFuncB type="table" tableValues="0.098 0.894" />
          </feComponentTransfer>
        </filter>
      </svg>

      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <div className="text-center mb-16 md:mb-24">
          <span className="team-label block font-mono text-[11px] uppercase tracking-[0.2em] text-[#8C6634] mb-8">
            El equipo
          </span>
          {/* Titular largo: a diferencia de los de dos o tres palabras del
              resto del sitio, este necesita medida de línea y un cuerpo más
              contenido para no leerse como un párrafo gritado. Mismo criterio
              que el h2 de NewsletterSection. */}
          <h2 className="team-heading font-heading text-[#1a1a1a] text-[clamp(22px,2.8vw,36px)] leading-[1.35] tracking-[-0.01em] max-w-[820px] mx-auto mb-8">
            Una firma construida sobre la discreción, la experiencia y el compromiso profundo con las familias empresarias
          </h2>
          <div className="team-line w-10 h-px bg-[#C9A26E] mx-auto origin-center" />
        </div>

        <div className="space-y-16 md:space-y-24">
          {TEAM_GROUPS.map((grupo) => {
            const esSocios = grupo.size === 300;
            return (
              <div key={grupo.label} className="team-group">
                {/* Eyebrow + filete al resto del ancho: el mismo agrupador que
                    usaba la ficha anterior, ahora con el dorado corregido. */}
                <div className={`flex items-center gap-4 mb-10 md:mb-12 ${esSocios ? '' : 'max-w-[500px] mx-auto'}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C6634] whitespace-nowrap">
                    {grupo.label}
                  </span>
                  <span className="flex-1 h-px bg-[#8C6634]/20" aria-hidden="true" />
                </div>

                {/* Hairline grid: la misma gramática que #valores y que
                    .todd-ebooks-grid (gap de 1px sobre un fondo dorado tenue,
                    sin cajas ni sombras). Las tres rejillas quedan completas
                    (3 de 3, 2 de 2), así que no hay celdas vacías pintadas del
                    color del separador. */}
                <ul
                  className={`grid gap-px bg-[#8C6634]/[0.14] ${
                    esSocios ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 max-w-[500px] mx-auto'
                  }`}
                >
                  {/* La entrada por scroll anima el <li> (.team-item), nunca el
                      <img>: index.css pone `img { transition: opacity .3s }` a
                      todas las imágenes y eso choca con GSAP (ver
                      HeroSection.jsx:55-68). El <li> no tiene transiciones. */}
                  {grupo.members.map((m) => (
                    <li key={m.slug} className="team-item bg-[#f5f0e8]">
                      <a
                        href={m.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${m.name} — perfil de LinkedIn (se abre en una pestaña nueva)`}
                        className={`team-card group block no-underline text-center ${
                          esSocios ? 'p-6 md:p-8' : 'p-5 md:p-6'
                        }`}
                      >
                        {/* La caja cuadrada reserva el espacio ANTES de que
                            cargue la imagen. No es cosmético: con loading="lazy"
                            las 7 cajas colapsarían a 0px y todos los
                            ScrollTrigger de más abajo (#conocimiento, #contacto)
                            se calcularían sobre una altura de página falsa. */}
                        <div
                          className="team-tile aspect-square mx-auto"
                          style={{ maxWidth: `${grupo.size}px` }}
                        >
                          {m.photo === false ? (
                            <span
                              aria-hidden="true"
                              className="absolute inset-0 flex items-center justify-center font-heading italic text-[#C9A26E] tracking-[-0.02em]"
                              style={{ fontSize: `${Math.round(grupo.size * 0.27)}px` }}
                            >
                              {iniciales(m.name)}
                            </span>
                          ) : (
                            <img
                              src={`/assets/team/${m.slug}.jpg`}
                              srcSet={
                                m.hd
                                  ? `/assets/team/${m.slug}.jpg 1x, /assets/team/${m.slug}@2x.jpg 2x`
                                  : undefined
                              }
                              alt={`Retrato de ${m.name}`}
                              width={grupo.size}
                              height={grupo.size}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>

                        <div className="mt-6">
                          <span className="team-rule block w-8 h-px bg-[#C9A26E]/40 mx-auto mb-4" aria-hidden="true" />
                          <h3
                            className={`team-title font-heading text-[#1a1a1a] leading-[1.3] mb-2 ${
                              esSocios ? 'text-xl md:text-2xl' : 'text-base md:text-lg'
                            }`}
                          >
                            {m.name}
                          </h3>
                          <span className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#6E685F] mb-3">
                            {grupo.role}
                          </span>
                          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C6634]">
                            LinkedIn
                            <svg className="team-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
